import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Fragment, memo, useState, useEffect, useMemo, useCallback } from 'react';
import classnames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../lib/Web5Context";
import Filters, { FiltersProvider } from "../Filters";
import QuickFilters from "../QuickFilters";
import { intercomHideAndShow } from "../../utils/custom";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'SectionFilter');
function SectionFilter(_ref) {
  let {
    useQuickFilters,
    quickFilters,
    isOpen,
    onClose,
    onChange,
    quickSchema,
    schema,
    id,
    entityLogName,
    logEvent,
    className,
    initialValues = {},
    values: valuesProp,
    toggleQuickFilter,
    onReOrder,
    isPinnable = false,
    noFiltersProvider = false,
    defaultOpenFilters = {}
  } = _ref;
  const classes = useStyles();
  const {
    t
  } = useWeb5Context();
  const [openFilters, setOpenFilters] = useState(defaultOpenFilters);
  const rootClassName = classnames(classes.root, className, {
    [classes.filtersActive]: isOpen
  });
  const [values, setValues] = useState(initialValues);
  useEffect(() => {
    setValues(valuesProp);
  }, [valuesProp]);
  useEffect(() => {
    if (!schema || !openFilters.length || openFilters && Object.keys(openFilters).length || schema.length > 4) return;
    setOpenFilters(schema.reduce((obj, el) => _objectSpread(_objectSpread({}, obj), {}, {
      [el.id]: true
    }), {}));
  }, [openFilters, schema]);
  const finalSchema = useMemo(() => {
    schema === null || schema === void 0 ? void 0 : schema.map(el => {
      el.fields.map(field => {
        if (field !== null && field !== void 0 && field.inputProps) {
          field.inputProps.className = `section-filter__field__${field.id}`;
          field.inputProps.classes = {
            input: `section-filter__field__${field.id}--input`,
            clear: `section-filter__field__${field.id}--clear`,
            calendarIcon: `section-filter__field__${field.id}--calendar`,
            clockIcon: `section-filter__field__${field.id}--clock`
          };
        }
        return field;
      });
      // locator if filter is open or not
      if (openFilters[el.id]) {
        el.className = `section-filter__${el.id}--opened`;
      } else {
        el.className = `section-filter__${el.id}`;
      }
      return el;
    });
    return schema;
  }, [schema, openFilters]);
  const onChangeFilter = useCallback(_ref2 => {
    let {
      filter,
      value
    } = _ref2;
    const newValues = _objectSpread(_objectSpread({}, values), {}, {
      [filter.id]: value
    });
    if (value === null || value === undefined || value === '') delete newValues[filter.id];
    !valuesProp && setValues(newValues);
    onChange && onChange(newValues);
    if (entityLogName && logEvent) logEvent({
      event: entityLogName,
      functionality: 'filter'
    });
  }, [values, valuesProp, onChange, entityLogName, logEvent]);
  const onToggleFilter = useCallback(_ref3 => {
    let {
      filter,
      hasSubfilters = false,
      parentFilter = null,
      overrideOpen = false
    } = _ref3;
    let newOpenFilters = {};

    // Helper function to set the subfilters values
    const toggleSubfilters = function (subfilters, filter) {
      if (subfilters === void 0) {
        subfilters = {};
      }
      if (filter === void 0) {
        filter = null;
      }
      const newSubfilters = Object.entries(subfilters).reduce((obj, _ref4) => {
        let [key, value] = _ref4;
        obj[key] = key === filter ? !value : false; // Toggle the specified subfilter and close others
        return obj;
      }, {});
      if (filter && !newSubfilters.hasOwnProperty(filter)) {
        newSubfilters[filter] = true;
      }
      return newSubfilters;
    };
    if (!parentFilter) {
      // Toggle main level filters
      newOpenFilters = Object.entries(openFilters).reduce((obj, _ref5) => {
        let [key, value] = _ref5;
        let newValue;
        switch (true) {
          case key === filter && hasSubfilters:
            newValue = _objectSpread(_objectSpread({}, value), {}, {
              open: overrideOpen || !value.open
            });
            break;
          case key === filter && !hasSubfilters:
            newValue = overrideOpen || !value;
            break;
          default:
            newValue = typeof value === 'boolean' ? overrideOpen || false : _objectSpread(_objectSpread({}, value), {}, {
              open: overrideOpen || false
            });
            break;
        }
        obj[key] = newValue;
        return obj;
      }, {});
      if (!newOpenFilters.hasOwnProperty(filter)) {
        newOpenFilters[filter] = hasSubfilters ? {
          open: true,
          subfilters: {}
        } : true;
      }
    } else {
      // Toggle subfilters
      newOpenFilters = Object.entries(openFilters).reduce((obj, _ref6) => {
        let [key, value] = _ref6;
        if (key === parentFilter) {
          obj[key] = {
            open: true,
            subfilters: toggleSubfilters(value.subfilters, filter)
          };
        } else {
          obj[key] = typeof value === 'boolean' ? false : _objectSpread(_objectSpread({}, value), {}, {
            open: false
          });
        }
        return obj;
      }, {});
      if (!newOpenFilters.hasOwnProperty(parentFilter)) {
        newOpenFilters[parentFilter] = {
          open: true,
          subfilters: toggleSubfilters({}, filter)
        };
      }
    }
    setOpenFilters(newOpenFilters);
  }, [openFilters]);
  const onClearFilters = useCallback(() => {
    !valuesProp && setValues(initialValues);
    onChange(initialValues);
  }, [initialValues, onChange, valuesProp]);
  const labels = useMemo(() => ({
    titleFilters: t('cfm_label_filters'),
    clearAll: t('cfm_action_clear_filters')
  }), [t]);
  useEffect(() => {
    if (isOpen) {
      intercomHideAndShow('hide');
    } else {
      intercomHideAndShow('show');
    }
  }, [isOpen]);
  const FiltersProviderElement = noFiltersProvider ? Fragment : FiltersProvider;
  if (useQuickFilters) return /*#__PURE__*/React.createElement(FiltersProviderElement, null, /*#__PURE__*/React.createElement(QuickFilters, {
    filters: quickFilters,
    schema: schema,
    toggled: openFilters,
    onToggle: onToggleFilter,
    onChange: onChangeFilter,
    onClearAll: onClearFilters,
    values: values
  }));
  return /*#__PURE__*/React.createElement("main", {
    id: id,
    className: rootClassName
  }, /*#__PURE__*/React.createElement(FiltersProviderElement, null, /*#__PURE__*/React.createElement(Filters, {
    isOpen: isOpen,
    toggled: openFilters,
    schema: finalSchema,
    labels: labels,
    showAdd: false,
    onToggle: onToggleFilter,
    onChange: onChangeFilter,
    onClearAll: onClearFilters,
    onClose: onClose,
    values: values,
    useViewsInFilters: false,
    isPinnable: isPinnable,
    toggleQuickFilter: toggleQuickFilter,
    quickSchema: quickSchema,
    onReOrder: onReOrder
  })));
}
process.env.NODE_ENV !== "production" ? SectionFilter.propTypes = {} : void 0;
export default /*#__PURE__*/memo(SectionFilter);